/** Calendar-year quarters. Business dates are 'YYYY-MM-DD' strings; never parsed via Date. */

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function isLeap(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

function daysInMonth(y: number, m: number): number {
  if (m === 2) return isLeap(y) ? 29 : 28;
  return [4, 6, 9, 11].includes(m) ? 30 : 31;
}

function parseDate(date: string): { y: number; m: number; d: number } {
  const match = DATE_RE.exec(date);
  if (!match) throw new Error(`Invalid date '${date}', expected YYYY-MM-DD`);
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (m < 1 || m > 12 || d < 1 || d > daysInMonth(y, m)) {
    throw new Error(`Invalid date '${date}'`);
  }
  return { y, m, d };
}

const p2 = (n: number) => (n < 10 ? `0${n}` : String(n));
const p4 = (n: number) => String(n).padStart(4, '0');

export function quarterId(date: string): string {
  const { y, m } = parseDate(date);
  return `${p4(y)}-Q${Math.ceil(m / 3)}`;
}

export function quarterRange(id: string): { start: string; end: string } {
  const match = /^(\d{4})-Q([1-4])$/.exec(id);
  if (!match) throw new Error(`Invalid quarter id '${id}'`);
  const y = Number(match[1]);
  const q = Number(match[2]);
  const startM = (q - 1) * 3 + 1;
  const endM = startM + 2;
  return {
    start: `${p4(y)}-${p2(startM)}-01`,
    end: `${p4(y)}-${p2(endM)}-${p2(daysInMonth(y, endM))}`,
  };
}

export function monthId(date: string): string {
  const { y, m } = parseDate(date);
  return `${p4(y)}-${p2(m)}`;
}

export function monthRange(id: string): { start: string; end: string } {
  const match = /^(\d{4})-(\d{2})$/.exec(id);
  const m = match ? Number(match[2]) : 0;
  if (!match || m < 1 || m > 12) throw new Error(`Invalid month id '${id}'`);
  const y = Number(match[1]);
  return {
    start: `${p4(y)}-${p2(m)}-01`,
    end: `${p4(y)}-${p2(m)}-${p2(daysInMonth(y, m))}`,
  };
}

const lusakaFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Africa/Lusaka',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Today's business date in Lusaka (UTC+2, no DST) as YYYY-MM-DD. */
export function lusakaToday(now: Date = new Date()): string {
  const parts = lusakaFmt.formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function currentQuarterId(now: Date = new Date()): string {
  return quarterId(lusakaToday(now));
}

/** The Lusaka calendar day ('YYYY-MM-DD') of a moment in time (an ISO timestamp), never the UTC day. */
export function lusakaDay(iso: string): string {
  return lusakaToday(new Date(iso));
}

/** Day of the week of a business date: 0 = Sunday ... 6 = Saturday. Built from UTC parts so no time zone can shift the day. */
export function weekdayOf(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** The Sabbath (Saturday) on or before this date, as YYYY-MM-DD. */
export function lastSabbath(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const back = (weekdayOf(iso) + 1) % 7; // Saturday -> 0, Sunday -> 1, ... Friday -> 6
  return new Date(Date.UTC(y, m - 1, d - back)).toISOString().slice(0, 10);
}
